import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWeatherData, setLocation } from "../store/WeatherSlice";

export const useWeatherData = (selectedDate, selectedCity) => {
  const dispatch = useDispatch();
  const [processedData, setProcessedData] = useState({
    dailyForecasts: {},
    dailyForecastArray: [],
    today: { main: { temp_min: 0, temp_max: 0 } },
    yesterday: { min: 0, max: 0 },
    tomorrow: { min: 0, max: 0 },
  });

  const currentWeather = useSelector((state) => state.weather.currentWeather);
  const forecast = useSelector((state) => state.weather.forecast);
  const status = useSelector((state) => state.weather.status);
  const error = useSelector((state) => state.weather.error);
  const location = useSelector((state) => state.weather.location);

  useEffect(() => {
    if (selectedCity && selectedCity.name && selectedCity.name !== location) {
      dispatch(setLocation(selectedCity.name));
    } else if (!selectedCity && location) {
      // If selectedCity is null (e.g., after clear), sync to Redux location
      dispatch(setLocation(location));
    } else if (status === "idle") {
      dispatch(fetchWeatherData());
    }
  }, [selectedCity, dispatch, status, location]);

  useEffect(() => {
    if (forecast && forecast.length > 0) {
      const dailyForecasts = forecast.reduce((acc, item) => {
        if (!item?.dt || !item?.main) return acc;

        const date = new Date(item.dt * 1000).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = {
            dt: item.dt,
            dt_txt: item.dt_txt,
            weather: item.weather?.[0] || {
              icon: "01d",
              description: "clear sky",
            },
            main: {
              temp_max: item.main.temp_max,
              temp_min: item.main.temp_min,
              temp: item.main.temp,
              feels_like: item.main.feels_like,
              humidity: item.main.humidity,
              pressure: item.main.pressure,
            },
            wind: {
              speed: item.wind?.speed,
              deg: item.wind?.deg,
              gust: item.wind?.gust,
            },
            visibility: item.visibility,
            clouds: item.clouds,
            uvi: item.uvi,
          };
        } else {
          acc[date].main.temp_max = Math.max(
            acc[date].main.temp_max,
            item.main.temp_max
          );
          acc[date].main.temp_min = Math.min(
            acc[date].main.temp_min,
            item.main.temp_min
          );
          if (item.uvi !== undefined) {
            acc[date].uvi = item.uvi;
          }
        }
        return acc;
      }, {});

      const dailyForecastArray = Object.values(dailyForecasts);
      const selectedDateStr = selectedDate.toLocaleDateString();
      const selectedDayForecast =
        dailyForecasts[selectedDateStr] || dailyForecastArray[0];
      const today = dailyForecastArray[0] || {
        main: { temp_min: 0, temp_max: 0 },
      };

      setProcessedData({
        dailyForecasts,
        dailyForecastArray,
        today: selectedDayForecast || today,
        yesterday: {
          min: today.main.temp_min - 2,
          max: today.main.temp_max + 1,
        },
        tomorrow: dailyForecastArray[1] || {
          min: today.main.temp_min,
          max: today.main.temp_max,
        },
      });
    }
  }, [forecast, selectedDate]);

  useEffect(() => {
    if (
      error &&
      (error.toLowerCase().includes("city not found") || error.toLowerCase().includes("404"))
    ) {
      // Show a more user-friendly error message for invalid city
      setProcessedData({
        dailyForecasts: {},
        dailyForecastArray: [],
        today: { main: { temp_min: 0, temp_max: 0 } },
        yesterday: { min: 0, max: 0 },
        tomorrow: { min: 0, max: 0 },
        cityError: `City not found. Please check the spelling or try another city.`,
      });
    }
  }, [error]);

  return { currentWeather, forecast, status, error, processedData };
};
